import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { ApolloQueryResult } from '@apollo/client';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { defaultTo, get } from 'lodash';
import classNames from 'classnames';
import Button from '@atlaskit/button/standard-button';
import { EntitlementDetails, LinkedDestinationEntitlement } from '@css-xp/graphtypes/src/generated-types-new';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { ViewPricingDetails } from '@css-xp/entitlements-v2/entitlement-page-utility/entitlement-summary/template/sections/pricing-section';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { CopyableText, formattedV2, Icon, SummarySectionV2, useSidebar } from '@css-xp/base';
import { getPartnerServiceDeskUrlForTrialExtension } from '@css-xp/ppc-navigation';
import { isEnterpriseInstanceOffering, isEnterpriseOffering } from '@css-xp/ppc-utils';
import { FormTrackerStepNames } from '../../sidebar-panel/change-subscription-pane/change-subscription-pane';
import { SIDEBAR } from '../../constants/sidebar';
import './trial-banner.scss';

export interface TrialBannerProps {
  entitlement: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountContext: TransactionAccountContextType;
  isDesignIteration1Enabled?: boolean;
  isTWCPhase2Enabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
}

const defaultSidebarOptions = {
  isOverlay: true,
  sidebarWidth: '600px',
};

const CollapsibleContainerSection = ({
  children,
  entitlement,
  isUpgradeTrial,
  onEndTrialClick,
  showSkipToPaidCTA,
  onSkipToPaidClick,
  expandText = 'View more',
  collapseText = 'View less',
}: {
  entitlement: EntitlementDetails;
  children: JSX.Element | string;
  isUpgradeTrial: boolean;
  onEndTrialClick: (event: React.MouseEvent<HTMLElement>) => void;
  showSkipToPaidCTA: boolean;
  onSkipToPaidClick: (event: React.MouseEvent<HTMLElement>) => void;
  expandText?: string;
  collapseText?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      {isExpanded ? children : null}
      <Button onClick={onClick} className="mt-8" testId="view-more-link" spacing="none" appearance="link">
        {isExpanded ? collapseText : expandText}
      </Button>
      {isExpanded && (isUpgradeTrial || entitlement?.isOptOutAllowed) ? (
        <Button
          onClick={onEndTrialClick}
          className="mt-8 ml-50"
          testId="end-trial-link"
          spacing="none"
          appearance="link"
        >
          End trial
        </Button>
      ) : null}
      {isExpanded && showSkipToPaidCTA ? (
        <Button
          onClick={onSkipToPaidClick}
          className="mt-8 ml-50"
          testId="skip-to-paid-link"
          spacing="none"
          appearance="link"
        >
          Skip to paid
        </Button>
      ) : null}
    </>
  );
};

const NotApplicable = ({ value }: { value?: string }) => {
  return (
    <span className="data-unavailable" aria-label="not-applicable">
      {value ?? 'N/A'}
    </span>
  );
};

export const TrialBanner: React.FC<TrialBannerProps> = ({
  entitlement,
  refetch,
  linkedEntitlements,
  transactionAccountContext,
  isDesignIteration1Enabled,
  isTWCPhase2Enabled,
  isAppEditionSupportEnabled,
}) => {
  const { txaId } = useParams<{ txaId: string }>();
  const { openSidebar } = useSidebar();

  const isUpgradeTrial = !!entitlement.upgradeTrial;
  const upgradeTrial = defaultTo(get(entitlement, 'upgradeTrial'), null);
  const startTimeStamp = (isUpgradeTrial
    ? entitlement.upgradeTrial?.startTimestamp
    : entitlement.subscriptionTimestamps?.firstTrialStartTimestamp) as string;
  const endTimeStamp = (isUpgradeTrial
    ? entitlement.upgradeTrial?.endTimestamp
    : entitlement.subscriptionTimestamps?.lastTrialEndTimestamp) as string;
  const totalDaysInTrial = differenceInCalendarDays(parseISO(endTimeStamp), parseISO(startTimeStamp));
  // Enable it as part of https://hello.atlassian.net/browse/ITFO-9724
  // const showSkipToPaidCTA = entitlement?.billingFrequency !== BillingFrequency.ANNUAL && !isUpgradeTrial;
  const showSkipToPaidCTA = false;

  const getPricingPlan = useCallback(() => {
    const pricingPlan = isUpgradeTrial ? upgradeTrial?.pricingPlan : entitlement?.pricingPlan;
    const pricingPlanType = pricingPlan?.type;

    const offeringObj = {
      id: (isUpgradeTrial ? upgradeTrial?.offeringId : entitlement?.offering?.id) as string,
      name: (isUpgradeTrial ? upgradeTrial?.offeringName : entitlement.offering?.name) as string,
    };

    return pricingPlanType ? (
      <div className="trial-banner-pricing">
        <CopyableText>
          <strong>{sentenceCase(pricingPlanType)}</strong>
        </CopyableText>
        <ViewPricingDetails
          entitlement={{
            ...entitlement,
            pricingPlan: pricingPlan,
            offering: offeringObj,
          }}
          buttonSpacing="none"
          buttonAppearance={isDesignIteration1Enabled ? 'default' : 'link'}
          refetch={refetch}
          transactionId={txaId}
          linkedEntitlements={linkedEntitlements}
        />
      </div>
    ) : (
      <NotApplicable />
    );
  }, [entitlement]);

  // const Currency = React.memo(({ amount, currency }: { amount: number; currency?: string | null }) => {
  //   return (
  //     <>
  //       {currency ? currency + ' ' : ''}
  //       {Intl.NumberFormat('en-US', { useGrouping: false, maximumFractionDigits: 2 }).format(amount)}
  //     </>
  //   );
  // });
  // Currency.displayName = 'Currency';

  const onTrialExtendClick = () => {
    window.open(getPartnerServiceDeskUrlForTrialExtension(), '_blank');
  };

  const onEndTrialClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    openSidebar({
      sidebarType: SIDEBAR.OPTOUT_TRIAL,
      payload: {
        entitlementNumber: entitlement?.entitlementNumber,
        entitlementId: entitlement.id,
        entitlement,
        isDesignIteration1Enabled,
        isTWCPhase2Enabled,
        isAppEditionSupportEnabled,
        refetch,
      },
      sidebarOptions: isDesignIteration1Enabled ? undefined : defaultSidebarOptions,
    });
  };

  const onSkipToPaidClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const upgradeTrial = defaultTo(get(entitlement, 'upgradeTrial'), null);
    const newPlanLabel = isUpgradeTrial ? upgradeTrial?.offeringName : entitlement?.offering?.name;
    const newPlanValue = isUpgradeTrial ? upgradeTrial?.offeringId : entitlement?.offering?.id;
    const isAppEntitlement = entitlement?.isApp;

    openSidebar({
      sidebarType: SIDEBAR.CHANGE_SUBSCRIPTION,
      payload: {
        entitlement,
        transactionAccountContext,
        refetch,
        initialFormTrackerStep: isAppEntitlement ? FormTrackerStepNames.PROMOTIONS : FormTrackerStepNames.REVIEW,
        formState: {
          skipTrial: true,
          newPlan: {
            label: newPlanLabel,
            value: newPlanValue,
          },
        },
      },
      sidebarOptions: defaultSidebarOptions,
    });
  };

  const renderTotalDaysInTrial = () => {
    if (totalDaysInTrial) {
      return <strong>{totalDaysInTrial}</strong>;
    }

    if (isDesignIteration1Enabled) {
      return <strong>0</strong>;
    }

    return <NotApplicable value="0" />;
  };

  const isEnterpriseProductOrInstanceOrUpgradeTrial =
    isEnterpriseInstanceOffering(entitlement.edition) ||
    isEnterpriseOffering(entitlement.edition) ||
    (isUpgradeTrial && isEnterpriseInstanceOffering(entitlement.upgradeTrial?.offeringName));

  return (
    <div
      data-testid="trial-banner"
      className={classNames('entitlement-trial-banner', {
        'mb-20': isDesignIteration1Enabled,
        'entitlement-trial-banner-v2': isDesignIteration1Enabled,
      })}
    >
      <div className={isDesignIteration1Enabled ? 'flex' : 'flex-container'}>
        <div className="mr-8">
          <Icon type="info" size="medium" color={'B400'} />
        </div>
        <div className="trial-details">
          <div className="heading mb-8">Active trial on this subscription</div>
          {isUpgradeTrial ? (
            <div className="subheading">
              The customer is currently trialing a no-cost {upgradeTrial?.offeringName || 'Premium'} plan. They&apos;ll
              still be charged for their existing {entitlement?.offering?.name ?? entitlement?.edition} plan.
            </div>
          ) : (
            <div className="subheading">
              The customer is currently trialing a no-cost {entitlement?.offering?.name ?? entitlement?.edition} plan.
            </div>
          )}
          <CollapsibleContainerSection
            entitlement={entitlement}
            isUpgradeTrial={isUpgradeTrial}
            onEndTrialClick={onEndTrialClick}
            showSkipToPaidCTA={showSkipToPaidCTA}
            onSkipToPaidClick={onSkipToPaidClick}
            expandText={isDesignIteration1Enabled ? 'View trial details' : 'View more'}
          >
            <SummarySectionV2.SummarySection
              testId="summary"
              className="summary-section-wrapper"
              columns={isDesignIteration1Enabled ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)'}
            >
              {isDesignIteration1Enabled ? (
                <SummarySectionV2.SummaryGridCalloutItem testId="summary-offering-name">
                  <div className="flex-container">
                    <label>Plan</label>
                    <div className="upgrade-plan-column">
                      <CopyableText>
                        <strong>{isUpgradeTrial ? upgradeTrial?.offeringName : entitlement.edition}</strong>
                      </CopyableText>
                    </div>
                  </div>
                </SummarySectionV2.SummaryGridCalloutItem>
              ) : (
                <SummarySectionV2.SummaryGridCalloutItem testId="summary-offering-name">
                  <div className="flex-container">
                    <label>Plan and trial period</label>
                    <div className="upgrade-plan-column">
                      <CopyableText>
                        <strong>{isUpgradeTrial ? upgradeTrial?.offeringName : entitlement.edition}</strong>
                      </CopyableText>
                      {startTimeStamp && endTimeStamp ? (
                        <CopyableText>
                          <formattedV2.FormattedDateRange start={startTimeStamp} end={endTimeStamp} />
                        </CopyableText>
                      ) : (
                        <NotApplicable />
                      )}
                    </div>
                  </div>
                </SummarySectionV2.SummaryGridCalloutItem>
              )}

              {isDesignIteration1Enabled && (
                <SummarySectionV2.SummaryGridCalloutItem testId="summary-offering-name">
                  <div className="flex-container">
                    <label>Trial period</label>
                    <div className="upgrade-plan-column">
                      {startTimeStamp && endTimeStamp ? (
                        <CopyableText>
                          <strong>
                            <formattedV2.FormattedDateRange start={startTimeStamp} end={endTimeStamp} />
                          </strong>
                        </CopyableText>
                      ) : (
                        <NotApplicable />
                      )}
                    </div>
                  </div>
                </SummarySectionV2.SummaryGridCalloutItem>
              )}

              <SummarySectionV2.SummaryGridCalloutItem testId="summary-pricing-plan">
                <div className="flex-container">
                  <label>Pricing</label>
                  {getPricingPlan()}
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-total-days-in-trial">
                <div className="flex-container">
                  <label>Total days on trial</label>
                  <div className="trial-column">
                    {renderTotalDaysInTrial()}
                    {!isEnterpriseProductOrInstanceOrUpgradeTrial ? (
                      <Button
                        appearance={isDesignIteration1Enabled ? 'default' : 'link'}
                        spacing="none"
                        className="inner-text ml-16"
                        onClick={onTrialExtendClick}
                      >
                        Extend
                      </Button>
                    ) : null}
                  </div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
            </SummarySectionV2.SummarySection>
          </CollapsibleContainerSection>
        </div>
      </div>
    </div>
  );
};
